import { Injectable } from '@angular/core';
import {
  Auth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  indexedDBLocalPersistence,
  initializeAuth,
  OAuthProvider,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User
} from 'firebase/auth';
// Static and named on purpose: a dynamic import() of this entry keeps every firebase/auth export alive.
import { _castAuth, _getInstance, type PopupRedirectResolverInternal } from '@firebase/auth/internal';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthUser } from './auth-user.model';

/**
 * Owns the {@link FirebaseApp}. The only file in the app allowed to import `firebase/app` /
 * `firebase/auth` — every other file works with {@link AuthUser} and this wrapper's async API
 * instead of the SDK directly. `firebase/messaging` is imported in exactly one other place,
 * `core/push/web-push.service.ts`, which takes the app from here.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
  public readonly app: FirebaseApp = initializeApp(environment.firebase);
  /**
   * `getAuth()` minus its popup resolver: with one, Firebase loads the popup's gapi + auth iframe on
   * every phone and Safari before the first auth state — and every route waits for that state. The
   * resolver goes to the popup calls instead, its iframe is loaded by {@link prewarmPopupSignInAsync}.
   */
  private readonly auth: Auth = initializeAuth(this.app, {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence]
  });
  private readonly currentUserSubject = new ReplaySubject<AuthUser | null>(1);

  /**
   * uid of the last emitted user (`null` = signed out, `undefined` = nothing emitted yet).
   * Startup can deliver the same user more than once (hydration + the SDK listener); consumers
   * only key off uid / signed-in state, so a duplicate emission carries no information and
   * would only restart downstream `switchMap` chains for nothing.
   */
  private lastEmittedUid: string | null | undefined = undefined;

  /** Never emits before the persisted session is restored; same-uid emissions are dropped. */
  public readonly currentUser$: Observable<AuthUser | null> = this.currentUserSubject.asObservable();

  public constructor() {
    onAuthStateChanged(this.auth, (user) => this.publish(user));
  }

  public getCurrentUser(): Promise<AuthUser | null> {
    return firstValueFrom(this.currentUser$);
  }

  /**
   * Returns the Firebase ID token, or null if one cannot be produced. A missing token is not
   * necessarily an error — Firebase refreshes it over the network near its one-hour expiry, so
   * this can fail purely because of the connection.
   */
  public async getIdTokenAsync(forceRefresh: boolean = false): Promise<string | null> {
    try {
      return (await this.auth.currentUser?.getIdToken(forceRefresh)) ?? null;
    } catch {
      return null;
    }
  }

  public hasSignedInUser(): boolean {
    return this.auth.currentUser !== null;
  }

  public async signInWithEmailAndPasswordAsync(email: string, password: string): Promise<AuthUser> {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);
    return this.publish(credential.user) as AuthUser;
  }

  public async signInWithGoogleAsync(): Promise<AuthUser> {
    const credential = await signInWithPopup(this.auth, new GoogleAuthProvider(), browserPopupRedirectResolver);
    return this.publish(credential.user) as AuthUser;
  }

  public async signInWithAppleAsync(): Promise<AuthUser> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const credential = await signInWithPopup(this.auth, provider, browserPopupRedirectResolver);
    return this.publish(credential.user) as AuthUser;
  }

  /**
   * Loads the popup's auth iframe ahead of a Google/Apple click — called by the pages that offer
   * them. `signInWithPopup` loads it before `window.open` when it is missing, and on Safari/iOS that
   * network wait outlasts the click's user activation, so the popup gets blocked. The SDK has no
   * public call for it (`getRedirectResult` loads it only for a pending redirect), hence its
   * internal entry — the same `_initialize` `getAuth()` ran at startup, on the same browsers
   * (`_shouldInitProactively`: phones and Safari; desktop Chrome loads it on click); firebase is pinned.
   */
  public async prewarmPopupSignInAsync(): Promise<void> {
    try {
      await this.auth.authStateReady();
      const resolver = _getInstance<PopupRedirectResolverInternal>(browserPopupRedirectResolver);
      if (resolver._shouldInitProactively) {
        await resolver._initialize(_castAuth(this.auth));
      }
    } catch (error) {
      // Not fatal: the click loads the iframe itself, which only Safari/iOS may then block.
      console.warn('Prewarming the sign-in popup failed:', error);
    }
  }

  public async createUserWithEmailAndPasswordAsync(email: string, password: string): Promise<AuthUser> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    return this.publish(credential.user) as AuthUser;
  }

  /** Developer login (bpp-auth dashboard, "als Person einloggen"): the custom token from the dev-login code exchange. */
  public async signInWithCustomTokenAsync(customToken: string): Promise<AuthUser> {
    const credential = await signInWithCustomToken(this.auth, customToken);
    return this.publish(credential.user) as AuthUser;
  }

  /**
   * The `devLoginExpiresAt` claim (unix seconds) of a developer login session; null for a regular
   * session, or when the ID token cannot be read (same network caveat as {@link getIdTokenAsync}).
   */
  public async getDevLoginExpiresAtAsync(): Promise<number | null> {
    try {
      const claim = (await this.auth.currentUser?.getIdTokenResult())?.claims['devLoginExpiresAt'];
      return typeof claim === 'number' ? claim : null;
    } catch {
      return null;
    }
  }

  public async signOutAsync(): Promise<void> {
    await signOut(this.auth);
    this.publish(null);
  }

  /** Maps to {@link AuthUser}, drops a same-uid re-emission, and publishes the rest. */
  private publish(user: User | null): AuthUser | null {
    const mapped: AuthUser | null = user ? { uid: user.uid, email: user.email } : null;
    const uid = mapped?.uid ?? null;
    if (this.lastEmittedUid === uid) {
      return mapped;
    }
    this.lastEmittedUid = uid;
    this.currentUserSubject.next(mapped);
    return mapped;
  }
}
